import type { NitroFetchOptions } from 'nitropack'
import { z } from 'zod'
import { jsonToFormData } from '~/utils/form/json-to-form-data'
import { apiResponseSchema } from '../helper/api-response-schema'
import { apiInstance } from '../instance'
import { accountDataSchema } from '../schemas/account-data'
import { accountListDataSchema } from '../schemas/account-list-data'
import {
  createAccountRequestSchema,
  type CreateAccountRequest
} from '../schemas/create-account-request'
import {
  createParameterdRequestSchema,
  type CreateParameterdRequest
} from '../schemas/create-parameterd-request'
import {
  createParametermRequestSchema,
  type CreateParametermRequest
} from '../schemas/create-parameterm-request'
import {
  createProductMainRequestSchema,
  type CreateProductMainRequest
} from '../schemas/create-product-main-request'
import {
  createProductPagesRequestSchema,
  type CreateProductPagesRequest
} from '../schemas/create-product-pages-request'
import {
  createProductSubRequestSchema,
  type CreateProductSubRequest
} from '../schemas/create-product-sub-request'
import {
  createRoleRequestSchema,
  type CreateRoleRequest
} from '../schemas/create-role-request'
import type { CreateServiceFileRequest } from '../schemas/create-service-file-request'
import {
  createServiceIssueRequestSchema,
  type CreateServiceIssueRequest
} from '../schemas/create-service-issue-request'
import {
  createServiceLogRequestSchema,
  type CreateServiceLogRequest
} from '../schemas/create-service-log-request'
import { forgetPasswordResponseSchema } from '../schemas/forget-password-response'
import {
  getAccountListRequestSchema,
  type GetAccountListRequest
} from '../schemas/get-account-list-request'
import {
  getParameterdListRequestSchema,
  type GetParameterdListRequest
} from '../schemas/get-parameterd-list-request'
import {
  getProductMainListRequestSchema,
  type GetProductMainListRequest
} from '../schemas/get-product-main-list-request'
import {
  getProductMainRequestSchema,
  type GetProductMainRequest
} from '../schemas/get-product-main-request'
import {
  getProductPagesListRequestSchema,
  type GetProductPagesListRequest
} from '../schemas/get-product-pages-list-request'
import {
  getProductSubListRequestSchema,
  type GetProductSubListRequest
} from '../schemas/get-product-sub-list-request'
import type { GetProductVersionRequest } from '../schemas/get-product-version-request'
import {
  getRoleListRequestSchema,
  type GetRoleListRequest
} from '../schemas/get-role-list-request'
import {
  getServiceIssueListRequestSchema,
  type GetServiceIssueListRequest
} from '../schemas/get-service-issue-list-request'
import { isAccountExistResponseSchema } from '../schemas/is-account-exist-response'
import { parameterdDataSchema } from '../schemas/parameterd-data'
import { parameterdListDataSchema } from '../schemas/parameterd-list-data'
import { parametermDataSchema } from '../schemas/parameterm-data'
import { productMainDataSchema } from '../schemas/product-main-data'
import { productMainListDataSchema } from '../schemas/product-main-list-data'
import { productPagesDataSchema } from '../schemas/product-pages-data'
import { productPagesListDataSchema } from '../schemas/product-pages-list-data'
import { productSubDataSchema } from '../schemas/product-sub-data'
import { productSubListDataSchema } from '../schemas/product-sub-list-data'
import { productVersionListDataSchema } from '../schemas/product-version-list-data'
import { roleDataSchema } from '../schemas/role-data'
import { roleListDataSchema } from '../schemas/role-list-data'
import { serviceFileDataSchema } from '../schemas/service-file-data'
import { serviceIssueDataSchema } from '../schemas/service-issue-data'
import { serviceIssueListDataSchema } from '../schemas/service-issue-list-data'
import { serviceLogListDataSchema } from '../schemas/service-log-list-data'
import {
  updateProductPagesRequestSchema,
  type UpdateProductPagesRequest
} from '../schemas/update-product-pages-request'
import {
  updateProductSubRequestSchema,
  type UpdateProductSubRequest
} from '../schemas/update-product-sub-request'
import {
  updateServiceIssueRequestSchema,
  type UpdateServiceIssueRequest
} from '../schemas/update-service-issue-request'
import { upsertAccountResponseSchema } from '../schemas/upsert-account-response'
import { upsertParameterdResponseSchema } from '../schemas/upsert-parameterd-response'
import { upsertParametermResponseSchema } from '../schemas/upsert-parameterm-response'
import { upsertProductMainResponseSchema } from '../schemas/upsert-product-main-response'
import { upsertProductPagesResponseSchema } from '../schemas/upsert-product-pages-response'
import { upsertProductSubResponseSchema } from '../schemas/upsert-product-sub-response'
import { upsertRoleResponseSchema } from '../schemas/upsert-role-response'
import { upsertServiceIssueResponseSchema } from '../schemas/upsert-service-issue-response'
import { useApiFactory } from '../use-api-factory'

export function useApi() {
  const api = useApiFactory(apiInstance, {
    useToken: true
  })

  /**
   * 處理搜尋多筆條件，將 Array 轉成 String，移除 Null 或 空字串
   */
  const formatQuery = <T extends Record<string, any>>(
    data?: T
  ): T | undefined => {
    return data
      ? (Object.entries(data).reduce(
          (acc, [key, value]) => {
            if (
              value === '' ||
              value == null ||
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === 'object' && Object.keys(value).length === 0)
            ) {
              return acc
            }

            if (Array.isArray(value)) {
              acc[key] = value.join(',')
            } else {
              acc[key] = value
            }

            return acc
          },
          {} as Record<string, any>
        ) as T)
      : data
  }

  return {
    /**
     * 帳號 - 新增
     */
    postAccount: (
      data: CreateAccountRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('account', jsonToFormData(data, ['picture']), options, {
        responseSchema: apiResponseSchema(upsertAccountResponseSchema),
        bodySchema: createAccountRequestSchema
      }),
    /**
     * 帳號 - 更新
     */
    patchAccount: (
      accountNo: string,
      data: Partial<CreateAccountRequest>,
      options?: NitroFetchOptions<string>
    ) => {
      return api.post(
        `account/${accountNo}`,
        jsonToFormData(data, ['picture']),
        options,
        {
          responseSchema: apiResponseSchema(upsertAccountResponseSchema),
          bodySchema: createAccountRequestSchema.partial()
        }
      )
    },
    /**
     * 帳號 - 查詢多筆
     */
    getAccountList: (
      query: GetAccountListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'account',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(accountListDataSchema.array()),
          querySchema: getAccountListRequestSchema
        }
      ),
    /**
     * 帳號 - 查詢單筆
     */
    getAccount: (accountNo: string, options?: NitroFetchOptions<string>) =>
      api.get(`account/${accountNo}`, options, {
        responseSchema: apiResponseSchema(accountDataSchema)
      }),
    /**
     * 查詢帳號是否存在
     */
    isAccountExist: (accountNo: string, options?: NitroFetchOptions<string>) =>
      api.get(
        'account/isClient',
        {
          ...options,
          query: {
            accountNo
          }
        },
        {
          responseSchema: apiResponseSchema(isAccountExistResponseSchema)
        }
      ),
    /**
     * 忘記密碼
     */
    forgotPassword: (accountNo: string, options?: NitroFetchOptions<string>) =>
      api.get(`account/forgotPassword/${accountNo}`, options, {
        responseSchema: apiResponseSchema(forgetPasswordResponseSchema)
      }),
    /**
     * 角色 - 新增
     */
    postRole: (data: CreateRoleRequest, options?: NitroFetchOptions<string>) =>
      api.post('role', data, options, {
        responseSchema: apiResponseSchema(upsertRoleResponseSchema),
        bodySchema: createRoleRequestSchema
      }),
    /**
     * 角色 - 更新
     */
    patchRole: (
      key: string,
      data: Partial<CreateRoleRequest>,
      options?: NitroFetchOptions<string>
    ) =>
      api.patch(`role/${key}`, data, options, {
        responseSchema: apiResponseSchema(upsertRoleResponseSchema),
        bodySchema: createRoleRequestSchema.partial()
      }),
    /**
     * 角色 - 查詢多筆
     */
    getRoleList: (
      query?: GetRoleListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'role',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(roleListDataSchema.array()),
          querySchema: getRoleListRequestSchema
        }
      ),
    /**
     * 角色 - 查詢單筆
     */
    getRole: (roleCode: string, options?: NitroFetchOptions<string>) =>
      api.get(`role/${roleCode}`, options, {
        responseSchema: apiResponseSchema(roleDataSchema)
      }),
    /**
     * 角色 - 刪除
     */
    deleteRole: (roleCode: string, options?: NitroFetchOptions<string>) =>
      api.delete(`role/${roleCode}`, options, {
        responseSchema: apiResponseSchema(z.null())
      }),
    /**
     * 參數管理 - 新增
     */
    postParameterd: (
      data: CreateParameterdRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('parameterd', data, options, {
        responseSchema: apiResponseSchema(upsertParameterdResponseSchema),
        bodySchema: createParameterdRequestSchema
      }),
    /**
     * 參數管理 -  更新
     */
    patchParameterd: (
      key: string,
      data: Partial<CreateParameterdRequest>,
      options?: NitroFetchOptions<string>
    ) =>
      api.patch(`parameterd/${key}`, data, options, {
        responseSchema: apiResponseSchema(upsertParameterdResponseSchema),
        bodySchema: createParameterdRequestSchema.partial()
      }),
    /**
     * 參數管理 - 查詢多筆
     */
    getParameterdList: (
      query: GetParameterdListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'parameterd',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(parameterdListDataSchema.array()),
          querySchema: getParameterdListRequestSchema
        }
      ),
    /**
     * 參數管理 - 查詢單筆
     */
    getParameterd: (id: string, options?: NitroFetchOptions<string>) =>
      api.get(`parameterd/${id}`, options, {
        responseSchema: apiResponseSchema(parameterdDataSchema)
      }),
    /**
     * 參數類別 - 新增
     */
    postParameterm: (
      data: CreateParametermRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('parameterm', data, options, {
        responseSchema: apiResponseSchema(upsertParametermResponseSchema),
        bodySchema: createParametermRequestSchema
      }),
    /**
     * 參數類別 - 更新
     */
    patchParameterm: (
      pmCode: string,
      data: Partial<CreateParametermRequest>,
      options?: NitroFetchOptions<string>
    ) =>
      api.patch(`parameterm/${pmCode}`, data, options, {
        responseSchema: apiResponseSchema(upsertParametermResponseSchema),
        bodySchema: createParametermRequestSchema.partial()
      }),
    /**
     * 參數類別 - 查詢多筆
     */
    getParametermList: (options?: NitroFetchOptions<string>) =>
      api.get('parameterm', options, {
        responseSchema: apiResponseSchema(parametermDataSchema.array())
      }),
    /**
     * 參數類別 - 查詢單筆
     */
    getParameterm: (key: string, options?: NitroFetchOptions<string>) =>
      api.get(`parameterm/${key}`, options, {
        responseSchema: apiResponseSchema(parametermDataSchema)
      }),
    /**
     * 產品主類目 - 新增
     */
    postProductMain: (
      data: CreateProductMainRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('productmain', jsonToFormData(data, ['productLogo']), options, {
        responseSchema: apiResponseSchema(upsertProductMainResponseSchema),
        bodySchema: createProductMainRequestSchema
      }),
    /**
     * 產品主類目 - 更新
     */
    patchProductMain: (
      pmID: number,
      data: Partial<CreateProductMainRequest>,
      options?: NitroFetchOptions<string>
    ) =>
      api.post(
        `productmain/${pmID}`,
        jsonToFormData(data, ['productLogo']),
        options,
        {
          responseSchema: apiResponseSchema(upsertProductMainResponseSchema),
          bodySchema: createProductMainRequestSchema.partial()
        }
      ),
    /**
     * 產品主類目 - 查詢多筆
     */
    getProductMainList: (
      query?: GetProductMainListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'productmain',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(productMainListDataSchema.array()),
          querySchema: getProductMainListRequestSchema
        }
      ),
    /**
     * 產品主類目 - 查詢單筆
     */
    getProductMain: (
      pmID: number,
      query?: GetProductMainRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        `productmain/${pmID}`,
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(productMainDataSchema),
          querySchema: getProductMainRequestSchema
        }
      ),
    /**
     * 產品子類目 - 新增
     */
    postProductSub: (
      data: CreateProductSubRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('productsub', data, options, {
        responseSchema: apiResponseSchema(upsertProductSubResponseSchema),
        bodySchema: createProductSubRequestSchema
      }),
    /**
     * 產品子類目 - 更新
     */
    patchProductSub: (
      psID: number,
      data: UpdateProductSubRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.patch(`productsub/${psID}`, data, options, {
        responseSchema: apiResponseSchema(upsertProductSubResponseSchema),
        bodySchema: updateProductSubRequestSchema
      }),
    /**
     * 產品子類目 - 查詢多筆
     */
    getProductSubList: (
      query: GetProductSubListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'productsub',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(productSubListDataSchema.array()),
          querySchema: getProductSubListRequestSchema
        }
      ),
    /**
     * 產品子類目 - 查詢單筆
     */
    getProductSub: (
      psID: number,
      query?: GetProductSubListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        `productsub/${psID}`,
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(productSubDataSchema),
          querySchema: getProductSubListRequestSchema
        }
      ),
    /**
     * 產品子網頁 - 新增
     */
    postProductPages: (
      data: CreateProductPagesRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post(
        'productpages',
        jsonToFormData(data, ['uploadFilesEn', 'uploadFilesZh']),
        options,
        {
          responseSchema: apiResponseSchema(upsertProductPagesResponseSchema),
          bodySchema: createProductPagesRequestSchema
        }
      ),
    /**
     * 產品子網頁 - 更新
     */
    patchProductPages: (
      ppID: number,
      data: UpdateProductPagesRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post(
        `productpages/${ppID}`,
        jsonToFormData(data, ['uploadFilesEn', 'uploadFilesZh']),
        options,
        {
          responseSchema: apiResponseSchema(upsertProductPagesResponseSchema),
          bodySchema: updateProductPagesRequestSchema
        }
      ),
    /**
     * 產品子網頁 - 查詢多筆
     */
    getProductPagesList: (
      query: GetProductPagesListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'productpages',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(productPagesListDataSchema.array()),
          querySchema: getProductPagesListRequestSchema
        }
      ),
    /**
     * 產品子網頁 - 查詢單筆
     */
    getProductPages: (key: number, options?: NitroFetchOptions<string>) =>
      api.get(`productpages/${key}`, options, {
        responseSchema: apiResponseSchema(productPagesDataSchema)
      }),
    /**
     * 問題答覆 - 新增
     */
    postServiceIssue: (
      data: CreateServiceIssueRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('serviceissue', data, options, {
        responseSchema: apiResponseSchema(upsertServiceIssueResponseSchema),
        bodySchema: createServiceIssueRequestSchema
      }),
    /**
     * 問題答覆 - 更新
     */
    patchServiceIssue: (
      siID: number,
      data: UpdateServiceIssueRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.patch(`serviceissue/${siID}`, data, options, {
        responseSchema: apiResponseSchema(upsertServiceIssueResponseSchema),
        bodySchema: updateServiceIssueRequestSchema
      }),
    /**
     * 問題答覆 - 查詢多筆
     */
    getServiceIssueList: (
      query: GetServiceIssueListRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'serviceissue',
        { ...options, query: formatQuery(query) },
        {
          responseSchema: apiResponseSchema(serviceIssueListDataSchema.array()),
          querySchema: getServiceIssueListRequestSchema
        }
      ),
    /**
     * 問題答覆 - 查詢單筆
     */
    getServiceIssue: (siID: number, options?: NitroFetchOptions<string>) =>
      api.get(`serviceissue/${siID}`, options, {
        responseSchema: apiResponseSchema(serviceIssueDataSchema)
      }),
    /**
     * 問題答覆記錄 - 新增
     */
    postServiceLog: (
      data: CreateServiceLogRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('servicelog', data, options, {
        responseSchema: apiResponseSchema(z.number()),
        bodySchema: createServiceLogRequestSchema
      }),
    /**
     * 問題答覆記錄 - 查詢多筆
     */
    getServiceLogList: (siID: number, options?: NitroFetchOptions<string>) =>
      api.get(`servicelog/${siID}`, options, {
        responseSchema: apiResponseSchema(serviceLogListDataSchema.array())
      }),
    /**
     * 問題答覆附件檔案 - 新增
     */
    postServiceFile: (
      data: CreateServiceFileRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.post('servicefile', jsonToFormData(data, ['files']), options, {
        responseSchema: apiResponseSchema(serviceFileDataSchema)
      }),
    /** 產品版本 - 查詢多筆 */
    getProductVersion: (
      query: GetProductVersionRequest,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        'productversion',
        { ...options, query },
        {
          responseSchema: apiResponseSchema(
            productVersionListDataSchema.array()
          )
        }
      ),
    postSubscribe: (data: any, options?: NitroFetchOptions<string>) =>
      api.post('tpi/api/Newsletter/SendMail', data, options, {
        responseSchema: apiResponseSchema(z.any())
      }),
    postUnsubscribe: (data: any, options?: NitroFetchOptions<string>) =>
      api.post('tpi/api/Newsletter/SendCancelMail', data, options, {
        responseSchema: apiResponseSchema(z.any())
      }),
    postSubscribeValidate: (
      activationKey: string,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        `tpi/api/Newsletter/ConfirmSubscription/ConfirmSubscription?activationKey=${activationKey}`,
        options,
        {
          responseSchema: apiResponseSchema(z.any())
        }
      ),
    postUnsubscribeValidate: (
      activationKey: string,
      options?: NitroFetchOptions<string>
    ) =>
      api.get(
        `tpi/api/Newsletter/CancelSubscription?activationKey=${activationKey}`,
        options,
        {
          responseSchema: apiResponseSchema(z.any())
        }
      ),
    getDirectorList: (options?: NitroFetchOptions<string>) =>
      api.get('account/director', options, {
        responseSchema: apiResponseSchema(accountDataSchema.array())
      })
  }
}
